import { useCallback, useEffect, useState } from 'react'
import type { SurvenueDTO, QueteDTO } from '@whatremains/shared'
import { api, ErreurApi } from '../api'

/**
 * Le journal des quêtes.
 *
 * `08-INTERFACE.md` §6 : il dit **toujours où aller**, le mystère est dans le
 * monde et jamais dans la logistique. Et les quêtes ratées ou expirées y
 * restent : sinon l'échec ne veut rien dire.
 *
 * Rien n'annonce jamais ce qu'un choix va faire.
 */

const ETIQUETTE_ETAT: Record<QueteDTO['etat'], string> = {
  proposee: 'à prendre',
  en_cours: 'en cours',
  reussie: 'faite',
  echouee: 'ratée',
  expiree: 'trop tard',
}

function Fiche({
  id,
  q,
  surAction,
  surLieu,
  surCombat,
}: {
  id: string
  q: QueteDTO
  surAction: (action: () => Promise<QueteDTO | void>) => void
  surLieu: (code: string) => void
  surCombat: (adversaire: string) => void
}) {
  const terminee = q.etat !== 'en_cours' && q.etat !== 'proposee'

  return (
    <div className="region" style={{ opacity: terminee ? 0.55 : 1 }}>
      <div className="region-tete">
        <h3>
          {q.titre}
          {/*
            La marque d'une affaire Élite.

            Enzo : « que visuellement je puisse savoir si une quête est une
            quête élite ». Elle se lit du TYPE, qui existait déjà et voyageait
            déjà jusqu'ici — inventer un second champ pour dire la même chose
            aurait créé deux vérités à tenir d'accord.

            Accolée au titre et non posée en bandeau : on la voit en parcourant
            la liste, ce qui est le moment où l'on décide de s'y mettre.
          */}
          {q.type === 'elite' && <span className="marque-elite">Élite</span>}
        </h3>
        {/*
          D'où elle vient, quand elle vient de quelque part.

          Enzo : « je viens de faire La halle au nom d'un seul, je l'ai fini, et
          je la retrouve, je peux la refaire. C'est pas la première fois. » Rien
          n'avait bougé : il venait de conclure la quête PRÉCÉDENTE du chapelet,
          même donneur et même sujet. Une suite qui ne se présente pas comme
          une suite se lit comme une répétition.
        */}
        {q.suiteDe && (
          <div className="quete-suite">Suite de « {q.suiteDe} »</div>
        )}
        <div className="region-meta">
          <span>{ETIQUETTE_ETAT[q.etat]}</span>
          {/*
            Le niveau auquel elle SE JOUE, et d'où il vient.

            Une quête dépassée est remise à un cran sous toi : adversaires plus
            gros, récompense plus grosse. Sans cette mention, la récompense
            changerait sans raison visible, ce qui se lit comme un bogue.
          */}
          <span>
            niveau {q.niveauMin}
            {q.niveauEcrit < q.niveauMin && (
              <i style={{ color: 'var(--r7)', fontStyle: 'normal' }}>
                {' '}· remise à niveau, écrite pour le {q.niveauEcrit}
              </i>
            )}
          </span>
          {q.donneurNom && <span>{q.donneurNom}</span>}
          {q.lieuNom && <span>{q.lieuNom}</span>}
          {q.etat === 'en_cours' && <span>étape {q.etape} sur {q.etapes}</span>}
          {q.minutesRestantes !== null && (
            <span style={{ color: q.minutesRestantes < 15 ? 'var(--r6)' : 'var(--r7)' }}>
              {q.minutesRestantes} min
            </span>
          )}
        </div>
      </div>

      {/*
        La voix, puis la consigne. Deux blocs, pas un pavé.

        Les textes de quête s'écrivent en deux temps depuis la réécriture : le
        donneur parle entre guillemets, puis une ligne dit ce qu'on attend de
        toi. En base, une ligne blanche les sépare — et le rendu l'avalait,
        parce qu'un `<p>` en HTML ne voit pas les sauts de ligne du texte.

        Résultat vu à l'écran : « ...il n'y a pas de mesure. » Tiens l'autre
        extrémité... » collés dans le même paragraphe, ce qui annule exactement
        ce que la forme cherchait à rendre lisible.

        Le dernier bloc porte la consigne, et se distingue.
      */}
      <div className="region-texte">
        {q.texte.split(/\n\s*\n/).map((bloc, i, tous) => (
          <p key={i} className={i === tous.length - 1 && tous.length > 1 ? 'quete-consigne' : undefined}>
            {bloc}
          </p>
        ))}
      </div>

      {/*
        Ce que le donneur veut, ouvertement.

        Écrit pour les 152 personnes du monde depuis le premier jour, et jamais
        montré. Enzo : « on comprend pas qui ils sont, ce qu'ils font, ce qu'ils
        veulent ». Ça ne manquait pas, c'était invisible.
      */}
      {q.donneurVeut && q.donneurNom && (
        <p className="veut">
          <b>{q.donneurNom}</b> veut : {q.donneurVeut.toLowerCase()}
        </p>
      )}

      {q.courante && (
        <div style={{ borderTop: '1px solid var(--line)', padding: '16px 20px' }}>
          <p style={{ margin: '0 0 12px', fontSize: 15 }}>{q.courante.texte}</p>

          {/*
            Et ce qu'il faut faire, en clair.

            « J'ai 0/2, je sais pas ce que je dois faire. » Le texte littéraire
            reste — il est bon — mais il ne remplace pas la consigne. Le canon
            l'exige : « il dit toujours où aller, le mystère est dans le monde,
            jamais dans la logistique ».
          */}
          <p className="consigne">
            {q.courante.consigne}
            {q.courante.avancement && (
              <b className={q.courante.accomplie ? 'fait' : undefined}>
                {' '}{q.courante.avancement.fait} sur {q.courante.avancement.total}
              </b>
            )}
          </p>

          {q.courante.type === 'choix' ? (
            <div className="grille" style={{ marginTop: 6 }}>
              {q.courante.options.map((o) => (
                <button
                  key={o.code}
                  className="choix"
                  onClick={() => surAction(() => api.avancerQuete(id, q.code, o.code))}
                >
                  {o.texte}
                  {/*
                    De qui l'on se rapproche, et de qui l'on s'éloigne.

                    Ce n'était nulle part, et sans ça le choix n'en était pas
                    un : on tranchait entre deux phrases sans savoir quel camp
                    on prenait. De combien reste tu, et ce que ça coûtera chez
                    ceux qui les détestent aussi.
                  */}
                  {/*
                    Et quand le choix n'engage aucune puissance, on le dit.

                    Enzo, en jouant : « sur les choix je n'ai pas quelle faction
                    va aimer et pas aimer comme sur PC ». Vérification faite, la
                    quête qu'il venait de finir n'avait aucune faction sur ses
                    deux options — dire la vérité à Lorne ou non ne déplace
                    personne — et 21 options sur 139 sont dans ce cas.

                    L'absence de ligne ne se distinguait donc pas d'un défaut
                    d'affichage. Un choix qui n'engage personne est une
                    information ; ne rien montrer n'en est pas une.
                  */}
                  <span className="choix-touche">
                    {o.touche.length > 0 ? (
                      o.touche.map((t) => (
                        <em key={t.nom} className={t.pour ? 'pour' : 'contre'}>
                          {t.pour ? 'sert' : 'dessert'} {t.nom}
                          {t.metier && <span> · {t.metier}</span>}
                        </em>
                      ))
                    ) : (
                      <em className="personne">n’engage aucune faction</em>
                    )}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap', margin: 0 }}>
              {q.courante.lieuNom && q.courante.lieuCode && (
                <button className="discret" onClick={() => surLieu(q.courante!.lieuCode!)}>
                  → {q.courante.lieuNom}
                </button>
              )}
              {/* Le compte est déjà dit dans la consigne, avec son sujet. */}
              {q.courante.reste && !q.courante.avancement && (
                <span className="tenu" style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>
                  {q.courante.reste}
                </span>
              )}
              {/*
                Le combat se lance d'ici, sur le bon adversaire.

                Enzo : « aller le chercher, le battre, le rechercher, le
                rebattre, puis retourner dans les quêtes, ça n'a pas de sens ».
                L'étape savait déjà qui affronter ; le joueur devait retrouver ce
                nom à la main dans une liste de quarante-quatre adversaires
                répartis en sept régions, une fois par combat demandé.

                C'est exactement le même écran d'engagement qu'ailleurs — ce
                qu'il rapporte, où l'on se situe face à lui, l'approche à poser —
                à la liste près, qu'on saute puisque le choix est fait. Et la
                sortie du combat revient ici, pas sur la fiche de personnage.
              */}
              {q.courante.adversaireCode && !q.courante.accomplie && (
                <button
                  className="action"
                  onClick={() => surCombat(q.courante!.adversaireCode!)}
                >
                  Se battre
                </button>
              )}
              <button
                className={q.courante.adversaireCode && !q.courante.accomplie ? 'discret' : 'action'}
                disabled={!q.courante.accomplie}
                onClick={() => surAction(() => api.avancerQuete(id, q.code, null))}
              >
                {q.etape >= q.etapes ? 'Conclure' : 'Continuer'}
              </button>
            </p>
          )}
        </div>
      )}

      <div
        style={{
          borderTop: '1px solid var(--line)',
          padding: '11px 20px',
          display: 'flex',
          gap: 20,
          flexWrap: 'wrap',
          alignItems: 'center',
          fontFamily: 'var(--mono)',
          fontSize: 10.5,
          color: 'var(--ink-dim)',
        }}
      >
        <span>{q.recompense.or} or</span>
        <span>{q.recompense.experience} xp</span>
        {q.recompense.reputation.map((r) => (
          <span key={r.code}>
            {r.nom} {r.valeur > 0 ? `+${r.valeur}` : r.valeur}
          </span>
        ))}
        {q.note && <span className="tenu">{q.note}</span>}

        {q.etat === 'proposee' && (
          <button
            className="action"
            style={{ marginLeft: 'auto' }}
            onClick={() => surAction(() => api.accepterQuete(id, q.code))}
          >
            Accepter
          </button>
        )}
        {q.etat === 'en_cours' && (
          <button
            className="discret"
            style={{ marginLeft: 'auto' }}
            onClick={() => surAction(() => api.abandonnerQuete(id, q.code))}
          >
            Laisser tomber
          </button>
        )}
      </div>
    </div>
  )
}

export function Quetes({
  personnageId,
  surChangement,
  surSurvenue,
  surCarte,
  surCombat,
}: {
  personnageId: string
  surChangement: () => void
  /** Ce qui vient d'arriver, remonté pour être montré sur le moment. */
  surSurvenue: (s: SurvenueDTO) => void
  surCarte: () => void
  /** Ouvre l'engagement directement sur l'adversaire que l'étape demande. */
  surCombat: (adversaire: string) => void
}) {
  const [ici, setIci] = useState<QueteDTO[]>([])
  const [journal, setJournal] = useState<QueteDTO[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [pret, setPret] = useState(false)

  const recharger = useCallback(async () => {
    const [a, b] = await Promise.all([api.quetesIci(personnageId), api.journal(personnageId)])
    setIci(a)
    setJournal(b)
    setPret(true)
  }, [personnageId])

  useEffect(() => { void recharger() }, [recharger])

  async function agir(action: () => Promise<QueteDTO | void>) {
    setErreur(null)
    try {
      const apres = await action()
      // Le serveur ne remplit `survenue` que sur l'action qui vient de conclure
      // la quête. Une simple lecture de journal ne rejoue donc pas l'annonce.
      if (apres && 'survenue' in apres && apres.survenue) surSurvenue(apres.survenue)
      await recharger()
      surChangement()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    }
  }

  if (!pret) return <div className="cadre"><div className="entete"><p className="tenu">…</p></div></div>

  const enCours = journal.filter((q) => q.etat === 'en_cours')
  const closes = journal.filter((q) => q.etat !== 'en_cours')

  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">
          {enCours.length} en cours · {ici.length} à prendre ici
        </div>
        <h1>Quêtes</h1>
        {erreur && <p className="erreur">{erreur}</p>}
      </div>

      {enCours.length > 0 && (
        <section>
          <div className="surtitre">En cours</div>
          {enCours.map((q) => (
            <Fiche key={q.code} id={personnageId} q={q} surAction={agir} surLieu={() => surCarte()} surCombat={surCombat} />
          ))}
        </section>
      )}

      <section>
        <div className="surtitre">Ici</div>
        {ici.length === 0 ? (
          <p className="tenu">Personne n’a rien à te demander.</p>
        ) : (
          ici.map((q) => <Fiche key={q.code} id={personnageId} q={q} surAction={agir} surLieu={() => surCarte()} surCombat={surCombat} />)
        )}
      </section>

      {closes.length > 0 && (
        <section>
          <div className="surtitre">Derrière toi</div>
          {closes.map((q) => (
            <Fiche key={q.code} id={personnageId} q={q} surAction={agir} surLieu={() => surCarte()} surCombat={surCombat} />
          ))}
        </section>
      )}
    </div>
  )
}
